/** @jsxImportSource @emotion/react */

import { FC, useEffect, useState } from 'react'
import { Button, Divider, Modal, Typography } from '@mui/material'
import { Form as FinalForm } from 'react-final-form'
import { useApolloClient, useMutation } from '@apollo/client'
import arrayMutators from 'final-form-arrays'
import { omit } from 'lodash'
import { DROPZONE_OPTIONS } from 'pagesNew/Questions/Form/consts'
import { validateRequired, validateUrl } from 'utils/validation'
import DropzoneField, { ImageField } from 'components/DropzoneField'
import { CreateInfluencerHub, CreateInfluencerHubVariables } from '__generatedTypes__/CreateInfluencerHub'
import { CREATE_INFLUENCER_HUB, FIND_ALL_INFLUENCER_HUB, UPDATE_INFLUENCER_HUB } from 'queries/influencerHub'
import { UpdateInfluencerHub, UpdateInfluencerHubVariables } from '__generatedTypes__/UpdateInfluencerHub'
import { toast } from 'utils/toast'
import { CreateInfluencerHubInput, InfluencerPlatformEnum } from '__generatedTypes__/globalTypes'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { INFLUENCER_HUB_PATH_S3 } from 'constants/images'
import { setFieldData } from 'utils/setFieldData'
import Loader from 'components/Loader'
import { isImageUploadError, loadImageFile, uploadImageFile } from 'utils/imageFile'
import * as classes from './styles'

export type InitialValues = CreateInfluencerHubInput & { id?: string }

export type FormValues = Omit<CreateInfluencerHubInput, 'coverImage' | 'influencerPlatform'> & {
  id?: string
  coverImage?: ImageField[]
  influencerPlatform?: { id: InfluencerPlatformEnum; title: string }
}

const INFLUENCER_PLATFORMS = [
  { title: 'Instagram', id: InfluencerPlatformEnum.Instagram },
  { title: 'TikTok', id: InfluencerPlatformEnum.TikTok },
  { title: 'YouTube', id: InfluencerPlatformEnum.Youtube },
]

type Props = {
  isOpen: boolean
  initialValues?: InitialValues | null
  onClose: () => void
}

export const InfluencerFormModal: FC<Props> = ({ isOpen, initialValues: initialValuesProp, onClose }) => {
  const [isLoadingImage, setIsLoadingImage] = useState(false)
  const [createInfluencerHub] = useMutation<CreateInfluencerHub, CreateInfluencerHubVariables>(CREATE_INFLUENCER_HUB)
  const [updateInfluencerHub] = useMutation<UpdateInfluencerHub, UpdateInfluencerHubVariables>(UPDATE_INFLUENCER_HUB)
  const [initialValues, setInitialValues] = useState<FormValues>()

  const apolloClient = useApolloClient()

  useEffect(() => {
    ;(async () => {
      if (initialValuesProp?.coverImage) {
        try {
          setIsLoadingImage(true)
          const coverImage = await loadImageFile(initialValuesProp.coverImage)

          setInitialValues({
            ...omit(initialValuesProp, '__typename'),
            influencerPlatform: INFLUENCER_PLATFORMS.find(
              (platform) => platform.id === initialValuesProp.influencerPlatform
            ),
            coverImage: coverImage ? [coverImage] : [],
          })
        } finally {
          setIsLoadingImage(false)
        }
      } else {
        setInitialValues({
          order: initialValuesProp?.order,
          influencerType: initialValuesProp?.influencerType,
        })
      }
    })()
  }, [isOpen])

  const onSubmit = async (values: FormValues) => {
    try {
      const coverImageUrl = await uploadImageFile(values.coverImage?.[0], INFLUENCER_HUB_PATH_S3, true)

      const variables = {
        ...values,
        influencerPlatform: values?.influencerPlatform?.id,
        coverImage: coverImageUrl,
      }

      if (values.id) {
        await updateInfluencerHub({
          variables: {
            updateInfluencerHubInputFieldName: variables,
          },
        })
      } else {
        const addedInfluencer = await createInfluencerHub({
          variables: {
            createInfluencerHubInput: variables,
          },
        })

        apolloClient.cache.updateQuery(
          {
            query: FIND_ALL_INFLUENCER_HUB,
            variables: {
              query: {},
            },
          },
          (data) => ({
            findAllInfluencerHub: {
              count: data.findAllInfluencerHub.count + 1,
              items: [...data.findAllInfluencerHub.items, addedInfluencer.data?.createInfluencerHub],
            },
          })
        )
      }

      onClose()
    } catch (error) {
      toast.error(isImageUploadError(error) ? error.message : 'Failed save influencer info')
    }
  }

  const isCreation = !initialValuesProp?.id

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div css={classes.container}>
        {isLoadingImage ? (
          <Loader />
        ) : (
          <FinalForm<FormValues>
            initialValues={initialValues}
            mutators={{ ...arrayMutators, setFieldData }}
            onSubmit={onSubmit}
            render={({ handleSubmit, submitting }) => (
              <form css={classes.form}>
                <Typography css={classes.title}>Influencer Info</Typography>

                <Divider />

                <InputField name="name" label="Influencer Name" fullWidth validate={validateRequired} />

                <DropzoneField
                  label="Cover Image"
                  name="coverImage"
                  validate={validateRequired}
                  dropzoneOptions={DROPZONE_OPTIONS}
                  options={{
                    maxFiles: 1,
                    sliceFromEnd: true,
                  }}
                />

                <InputField name="description" label="Description" validate={validateRequired} fullWidth />

                <SelectField
                  name="influencerPlatform"
                  validate={validateRequired}
                  items={INFLUENCER_PLATFORMS}
                  label="Influencer Platform"
                  placeholder="Influencer Platform"
                  fullWidth
                />

                <InputField name="socialLink" label="Social Link" fullWidth validate={validateUrl} />

                <div css={classes.actions}>
                  <Button
                    fullWidth
                    size="large"
                    color="blue"
                    variant="outlined"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>

                  <Button fullWidth size="large" color="blue" onClick={handleSubmit} disabled={submitting}>
                    {isCreation ? 'Create' : 'Confirm Edits'}
                  </Button>
                </div>
              </form>
            )}
          />
        )}
      </div>
    </Modal>
  )
}
